import { useEffect, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Grid, Stack, Tooltip, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import {
  Datagrid,
  DateInput,
  Filter,
  FunctionField,
  List,
  SelectInput,
  TextInput,
  TopToolbar,
  useListContext,
  useLocale,
  useNotify,
  useTranslate,
} from 'react-admin';
import { CustomBreadcrumbs } from 'src/components/custom-breadcrumbs';
import { DashboardContent } from 'src/layouts/dashboard';
import { PermissionGuard } from 'src/routes/components/permission-guard';
import { paths } from 'src/routes/paths';
import ErrorPage403 from 'src/pages/error/403';
import {
  StPoolRewardClaim,
  StPoolRewardClaimStats,
  StPoolRewardClaimStatus,
  stErrorMessage,
  stPoolRewardClaimStats,
} from '../../../services/socialTradingService';
import { Resources } from '../../resource';
import { StIdCell, StNameIdCell, stFormatDateTime, stText } from '../StListUi';
import { POOL_REWARD_CLAIM_STATUSES, POOL_REWARD_TARGET_ACCOUNTS } from './constants';
import {
  claimFailureReason,
  dayEndIso,
  dayStartIso,
  formatClaimAmount,
  formatCount,
  formatDecimalString,
  isoToDateInput,
  pickCycleLabel,
} from './helpers';
import './init';

const PERMISSION_RESOURCE = 'social-trading-settlement';

const FILTER_BAR_SX = {
  '& .RaFilter-form': {
    px: 2,
    pt: 2,
    pb: 1,
    columnGap: 2,
    rowGap: 1.5,
    alignItems: 'flex-end',
  },
};

const CLAIM_STATUS_COLOR: Record<StPoolRewardClaimStatus, ChipProps['color']> = {
  CLAIMABLE: 'warning',
  CLAIMING: 'info',
  CLAIMED: 'success',
};

const ListFilter = (props: any) => {
  const t = useTranslate();
  return (
    <Filter
      {...(props || {})}
    >
      <TextInput source="roomId" label={t('module.socialTrading.colRoomId')} alwaysOn resettable />
      <TextInput
        source="roomName"
        label={t('module.socialTrading.colRoomName')}
        alwaysOn
        resettable
      />
      <TextInput source="poolId" label={t('module.socialTrading.colPoolId')} alwaysOn resettable />
      <TextInput source="userId" label={t('module.socialTrading.colUserId')} alwaysOn resettable />
      <TextInput
        source="nickname"
        label={t('module.socialTrading.colNickname')}
        alwaysOn
        resettable
      />
      <SelectInput
        source="claimStatus"
        label={t('module.socialTrading.colClaimStatus')}
        choices={POOL_REWARD_CLAIM_STATUSES.map((id) => ({
          id,
          name: t(`module.socialTrading.claimStatus.${id}`),
        }))}
        alwaysOn
        resettable
        sx={{ minWidth: 160 }}
      />
      <SelectInput
        source="targetAccount"
        label={t('module.socialTrading.colTargetAccount')}
        choices={POOL_REWARD_TARGET_ACCOUNTS.map((id) => ({
          id,
          name: t(`module.socialTrading.targetAccount.${id}`),
        }))}
        alwaysOn
        resettable
        sx={{ minWidth: 180 }}
      />
      <DateInput
        source="claimedFrom"
        label={t('module.socialTrading.filterClaimedFrom')}
        parse={dayStartIso}
        format={isoToDateInput}
        alwaysOn
      />
      <DateInput
        source="claimedTo"
        label={t('module.socialTrading.filterClaimedTo')}
        parse={dayEndIso}
        format={isoToDateInput}
        alwaysOn
      />
    </Filter>
  );
};

function ResetFiltersButton() {
  const t = useTranslate();
  const { filterValues, setFilters } = useListContext();
  const hasFilters = Object.keys(filterValues ?? {}).length > 0;

  return (
    <TopToolbar sx={{ px: 2, pt: 2, pb: 1, alignSelf: 'flex-end' }}>
      <Button
        variant="outlined"
        size="medium"
        color="inherit"
        startIcon={<RestartAltIcon />}
        disabled={!hasFilters}
        onClick={() => setFilters({}, [])}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {t('module.socialTrading.resetFilters')}
      </Button>
    </TopToolbar>
  );
}

type StatTile = {
  key: keyof StPoolRewardClaimStats;
  label: string;
  value: string;
  note?: string;
  tooltip?: string;
};

const statAmount = (value: string | null | undefined): string =>
  value === null || value === undefined || value === ''
    ? '—'
    : `${formatDecimalString(value, 2)} USDC`;

function RewardClaimStatsPanel() {
  const t = useTranslate();
  const notify = useNotify();
  const [stats, setStats] = useState<StPoolRewardClaimStats | null>(null);

  useEffect(() => {
    let cancelled = false;
    stPoolRewardClaimStats()
      .then((res) => {
        if (!cancelled) setStats(res);
      })
      .catch((e) => {
        if (cancelled) return;
        setStats(null);
        notify(stErrorMessage(e), { type: 'error' });
      });
    return () => {
      cancelled = true;
    };
  }, [notify]);

  const overlap = t('module.socialTrading.kpiUsersOverlapHint');
  const tiles: StatTile[] = [
    {
      key: 'totalFinalReward',
      label: t('module.socialTrading.kpiTotalFinalReward'),
      value: statAmount(stats?.totalFinalReward),
    },
    {
      key: 'totalClaimedAmount',
      label: t('module.socialTrading.kpiTotalClaimed'),
      value: statAmount(stats?.totalClaimedAmount),
    },
    {
      key: 'totalUnclaimedAmount',
      label: t('module.socialTrading.kpiTotalUnclaimed'),
      value: statAmount(stats?.totalUnclaimedAmount),
      note: t('module.socialTrading.kpiTotalUnclaimedNote'),
    },
    {
      key: 'claimedUsers',
      label: t('module.socialTrading.kpiClaimedUsers'),
      value: formatCount(stats?.claimedUsers),
      tooltip: overlap,
    },
    {
      key: 'unclaimedUsers',
      label: t('module.socialTrading.kpiUnclaimedUsers'),
      value: formatCount(stats?.unclaimedUsers),
      tooltip: overlap,
    },
  ];

  return (
    <Box sx={{ px: 2, pt: 2 }}>
      <Grid container spacing={2}>
        {tiles.map((tile) => (
          <Grid item xs={12} sm={6} md={4} lg key={tile.key}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    {tile.label}
                  </Typography>
                  {tile.tooltip && (
                    <Tooltip title={tile.tooltip}>
                      <InfoOutlinedIcon
                        aria-label={tile.tooltip}
                        sx={{ fontSize: 16, color: 'text.disabled' }}
                      />
                    </Tooltip>
                  )}
                </Stack>
                <Typography variant="h5" sx={{ mt: 0.5 }}>
                  {tile.value}
                </Typography>
                {tile.note && (
                  <Typography variant="caption" color="text.secondary">
                    {tile.note}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {t('module.socialTrading.kpiUnfilteredNote')}
      </Typography>
    </Box>
  );
}

function ClaimStatusCell({ row }: { row: StPoolRewardClaim }) {
  const t = useTranslate();
  const failure = claimFailureReason(row);

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ whiteSpace: 'nowrap' }}>
      <Chip
        size="small"
        color={CLAIM_STATUS_COLOR[row.claimStatus] ?? 'default'}
        label={t(`module.socialTrading.claimStatus.${row.claimStatus}`, { _: row.claimStatus })}
      />
      {failure && (
        <Tooltip title={failure}>
          <Chip
            size="small"
            variant="outlined"
            color="error"
            label={t('module.socialTrading.claimFailed')}
          />
        </Tooltip>
      )}
    </Stack>
  );
}

function targetLabel(
  t: (key: string, options?: any) => string,
  account: string | null
): string | null {
  return account ? t(`module.socialTrading.targetAccount.${account}`, { _: account }) : null;
}

export default function SocialTradingPoolRewardClaimsList() {
  const t = useTranslate();
  const locale = useLocale();

  return (
    <PermissionGuard resource={PERMISSION_RESOURCE} action="read" fallback={<ErrorPage403 />}>
      <DashboardContent maxWidth={false}>
        <CustomBreadcrumbs
          heading={t('module.socialTrading.poolRewardClaims')}
          links={[
            { name: t('module.socialTrading.title'), href: paths.socialTrading.workbench.root },
            { name: t('module.socialTrading.poolRewardClaims') },
          ]}
        />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {t('module.socialTrading.poolRewardClaimsHint')}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          {t('module.socialTrading.rewardClaimScopeHint')}
        </Typography>

        <Card sx={{ mt: 2, pb: 2 }}>
          <RewardClaimStatsPanel />
          <List
            resource={Resources.socialTradingPoolRewardClaims}
            actions={<ResetFiltersButton />}
            empty={false}
            perPage={20}
            exporter={false}
            filters={<ListFilter />}
            sx={FILTER_BAR_SX}
          >
            <Box sx={{ overflow: 'auto', mt: 2 }}>
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                empty={
                  <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                    {t('module.socialTrading.emptyRewardClaims')}
                  </Typography>
                }
              >
                <FunctionField
                  label={t('module.socialTrading.colUser')}
                  render={(row: StPoolRewardClaim) => (
                    <StNameIdCell name={row.nickname} id={row.userId} />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colRoom')}
                  render={(row: StPoolRewardClaim) => (
                    <StNameIdCell name={row.roomName} id={row.roomId} />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colCycle')}
                  render={(row: StPoolRewardClaim) => (
                    <StNameIdCell
                      name={pickCycleLabel(row.cycleLabel, row.cycleLabelLang, locale)}
                      id={row.poolId}
                    />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colCycleEnd')}
                  sx={{ display: 'inline-block', minWidth: 150 }}
                  render={(row: StPoolRewardClaim) => stText(stFormatDateTime(row.cycleEnd))}
                />
                <FunctionField
                  label={t('module.socialTrading.colVolumeReward')}
                  sx={{ display: 'inline-block', minWidth: 100 }}
                  render={(row: StPoolRewardClaim) =>
                    stText(formatClaimAmount(row.volumeReward, row.currency))
                  }
                />
                <FunctionField
                  label={t('module.socialTrading.colProfitReward')}
                  sx={{ display: 'inline-block', minWidth: 100 }}
                  render={(row: StPoolRewardClaim) =>
                    stText(formatClaimAmount(row.profitReward, row.currency))
                  }
                />
                <FunctionField
                  label={t('module.socialTrading.colFinalReward')}
                  sx={{ display: 'inline-block', minWidth: 100 }}
                  render={(row: StPoolRewardClaim) =>
                    stText(formatClaimAmount(row.finalReward, row.currency))
                  }
                />
                <FunctionField
                  label={t('module.socialTrading.colClaimStatus')}
                  sx={{ display: 'inline-block', minWidth: 100 }}
                  render={(row: StPoolRewardClaim) => <ClaimStatusCell row={row} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colClaimedAmount')}
                  sx={{ display: 'inline-block', minWidth: 120 }}
                  render={(row: StPoolRewardClaim) =>
                    stText(formatClaimAmount(row.claimedAmount, row.currency))
                  }
                />
                <FunctionField
                  label={t('module.socialTrading.colTargetAccount')}
                  sx={{ whiteSpace: 'nowrap' }}
                  render={(row: StPoolRewardClaim) => stText(targetLabel(t, row.targetAccount))}
                />
                <FunctionField
                  label={t('module.socialTrading.colDestinationRef')}
                  render={(row: StPoolRewardClaim) => <StIdCell value={row.destinationRef} />}
                />
                <FunctionField
                  label={t('module.socialTrading.colTxHash')}
                  render={(row: StPoolRewardClaim) => (
                    <StIdCell value={row.txHash} head={10} tail={8} />
                  )}
                />
                <FunctionField
                  label={t('module.socialTrading.colClaimedAt')}
                  sx={{ display: 'inline-block', minWidth: 150 }}
                  render={(row: StPoolRewardClaim) => stText(stFormatDateTime(row.claimedAt))}
                />
              </Datagrid>
            </Box>
          </List>
        </Card>
      </DashboardContent>
    </PermissionGuard>
  );
}
